import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { ReactNode } from 'react';
import { MessageTranslator, localize } from '@oneflowab/pomes';

import { isDraft } from '@flow/libs/agreement';
import isDraftApproved from '@flow/libs/agreement/actions/is-draft-approved';
import { isDraftPartiallyApproved } from '@flow/libs/agreement/draft-approval-flow';
import { hasAnyPendingApproverApproved } from '@flow/libs/agreement/pending-state-flow';
import {
  getAgreementMyParticipant,
  getGuestToken,
  getParticipantParty,
  isAgreementOwner,
  isPartiallySigned,
} from '@flow/libs/agreement/selectors';
import {
  discardChanges,
  getAcceptedSuggestions,
  getDataFieldExternalKeyValueMap,
  getDataFieldExternalKeyMap,
  getPristineState,
  getPristineContractData,
  getRejectedSuggestions,
} from '@app/reducers/current-contract';
import useAgreement from '@app/hooks/use-agreement';
import useCurrentBoxes from '@app/hooks/use-current-boxes';
import useCurrentData from '@app/hooks/use-current-data';
import useIsInPreviewMode from '@app/hooks/use-is-in-preview-mode';
import { getAccountFromSessionSelector } from '@app/reducers/session';
import agreements from '@app/reducers/entities/agreements';
import { getNotifyParticipant } from '@app/reducers/editor';
import { getEmptyRequiredElementsCount } from '@app/reducers/helpers/current-contract';

import {
  useSaveAgreementHandler,
  NotificationData,
} from '@app/components/document-call-to-actions/actions/buttons-call-to-action/hooks/use-save-agreement-handler';
import useSaveChangesButton from '@app/components/document-call-to-actions/actions/buttons-call-to-action/hooks/use-save-changes-button';
import Button from '@app/components/button';
import SendContract from '@app/components/modals/send-contract';
import ResetDraftApprovalsWarning from '@app/components/modals/reset-draft-approvals/reset-draft-approvals';
import ResetApprovalsWarning from '@app/components/modals/reset-approvals';
import ResetApprovalsSignaturesWarning from '@app/components/modals/reset-approvals-signatures';
import ResetSignaturesWarning from '@app/components/modals/reset-signatures';
import Snackbar from '@app/components/snackbar';
import { SEVERITY_TYPES } from '@app/components/alert';
import { ApiError } from '@app/components/api-error';
import toast from '@app/components/toasts';
import { EMPTY_EDITABLE_REQUIRED_FIELDS_TOAST_ID } from '@app/components/document-call-to-actions/actions/buttons-call-to-action/buttons/sign/helpers';
import { PARTIALLY_SIGNED_TOAST_ID } from '@app/components/agreement-loader/partially-signed-toast';
import { UNASSIGNED_SIGNATURE_FIELDS_TOAST_ID } from '@app/components/contract-boxes/pdf-box/pdf-file/pdf-viewer/pdf-page/overlay-field/overlay-signature-field/unassigned-fields-toast';
import { getUnassignedSignatureFields } from '@app/components/contract-boxes/pdf-box/pdf-file/pdf-viewer/pdf-page/overlay-field/overlay-signature-field/unassigned-fields-toast/helpers';

import {
  showApprovalsResetToast,
  showApprovalsSignaturesResetToast,
  showEmptyRequiredFieldsToast,
  showPreviewModeSaveToast,
  showSaveSuccessToast,
  showSignaturesResetToast,
  showUnassignedSignatureFieldsToast,
} from '@app/components/document-call-to-actions/actions/save-changes-toasts';
import { getDefaultNotificationData, getError, hasValidContractChanges } from './helpers';

export type Props = {
  agreementId: number;
  onSaveAgreement?: () => void;
  message?: MessageTranslator;
  children: ReactNode;
  modalClassName?: string;
  buttonClassName?: string;
  buttonKind?: string;
  shouldShowSaveSuccessToast?: boolean;
};

export const SaveChangesButton = ({
  agreementId,
  onSaveAgreement,
  message,
  children,
  modalClassName,
  buttonClassName,
  buttonKind,
  shouldShowSaveSuccessToast = true,
}: Props) => {
  const dispatch = useDispatch();
  const agreement = useAgreement(agreementId);
  const boxMap = useCurrentBoxes();
  const pristineState = useSelector(getPristineState);
  const data = useCurrentData();

  const isNotifyParticipant = useSelector(getNotifyParticipant);

  const [isSendModalVisible, setIsSendModalVisible] = useState(false);
  const [isResetDraftApprovalModalVisible, setIsResetDraftApprovalModalVisible] = useState(false);
  const [isResetApprovalModalVisible, setIsResetApprovalModalVisible] = useState(false);
  const [isResetSignatureModalVisible, setIsResetSignatureModalVisible] = useState(false);
  const [isResetApprovalSignatureModalVisible, setIsResetApprovalSignatureModalVisible] =
    useState(false);

  const acceptedSuggestions = useSelector(getAcceptedSuggestions);
  const rejectedSuggestions = useSelector(getRejectedSuggestions);
  const pristineContract = useSelector(getPristineContractData);
  const hasValidChanges = hasValidContractChanges(
    rejectedSuggestions,
    acceptedSuggestions,
    pristineState,
    boxMap,
    pristineContract,
  );
  const checked = isNotifyParticipant && hasValidChanges;

  const { disabled } = useSaveChangesButton();

  const account = useSelector(getAccountFromSessionSelector);
  const saveRpcState = useSelector((state) =>
    agreements.getSaveAgreementSelector(state, {
      id: agreementId,
    }),
  );
  const resetSaveRpcState = () => {
    dispatch(
      agreements.saveAgreementReset({
        id: agreementId,
      }),
    );
  };
  const guestToken = useSelector(getGuestToken);
  const isGuest = Boolean(guestToken);
  const isOwner = isAgreementOwner(account, agreement);
  const myParticipant = getAgreementMyParticipant(agreement);
  const partiallySigned = isPartiallySigned(agreement);
  const dataFieldExternalKeyValueMap = useSelector(getDataFieldExternalKeyValueMap);
  const dataFieldExternalKeyMap = useSelector(getDataFieldExternalKeyMap);
  const isInPreviewMode = useIsInPreviewMode();

  const party = getParticipantParty(agreement, myParticipant?.agreementCompany.id) as Oneflow.Party;

  const onSuccess = useCallback(
    (props: any) => {
      onSaveAgreement?.(props);
      /**
       * Remove the "partially signed" toast when saving changes.
       * Saving resets signatures, so the document is no longer partially signed.
       * This prevents showing stale toast information to the user.
       */
      toast.remove({
        toastId: PARTIALLY_SIGNED_TOAST_ID,
      });

      if (
        isPartiallySigned(agreement) &&
        hasValidChanges &&
        hasAnyPendingApproverApproved(agreement)
      ) {
        showApprovalsSignaturesResetToast();
      } else if (hasAnyPendingApproverApproved(agreement)) {
        showApprovalsResetToast();
      } else if (isPartiallySigned(agreement) && hasValidChanges) {
        showSignaturesResetToast();
      }

      if (shouldShowSaveSuccessToast) {
        showSaveSuccessToast(agreement);
      }

      const { editableEmptyRequiredElementsCount } = getEmptyRequiredElementsCount({
        agreement,
        isGuest,
        boxMap,
        data,
        dataFieldExternalKeyMap,
        dataFieldExternalKeyValueMap,
      });
      if (editableEmptyRequiredElementsCount <= 0) {
        toast.remove({
          toastId: EMPTY_EDITABLE_REQUIRED_FIELDS_TOAST_ID,
        });
      } else if (editableEmptyRequiredElementsCount > 0) {
        showEmptyRequiredFieldsToast(editableEmptyRequiredElementsCount);
      }
      if (isOwner) {
        const unassignedFields = getUnassignedSignatureFields(props?.raw);
        if (unassignedFields.length <= 0) {
          toast.remove({
            toastId: UNASSIGNED_SIGNATURE_FIELDS_TOAST_ID,
          });
        } else {
          showUnassignedSignatureFieldsToast(unassignedFields);
        }
      }
    },
    [
      agreement,
      hasValidChanges,
      isOwner,
      isGuest,
      boxMap,
      data,
      dataFieldExternalKeyMap,
      dataFieldExternalKeyValueMap,
      onSaveAgreement,
      shouldShowSaveSuccessToast,
    ],
  );

  const { saveAgreementHandler } = useSaveAgreementHandler({
    agreementId,
    onSuccess,
  });

  const handleSaveChanges = () => {
    if (isInPreviewMode) {
      showPreviewModeSaveToast();
      dispatch(discardChanges(agreement));
      return;
    }

    if (
      partiallySigned &&
      hasAnyPendingApproverApproved(agreement) &&
      hasValidChanges &&
      modalClassName !== 'rich-text-region'
    ) {
      setIsResetApprovalSignatureModalVisible(true);
      return;
    }

    if (partiallySigned && hasValidChanges && modalClassName !== 'rich-text-region') {
      setIsResetSignatureModalVisible(true);
      return;
    }

    if (!isOwner) {
      saveAgreementHandler(
        getDefaultNotificationData(party, myParticipant, message) as NotificationData,
      );
      return;
    }

    if (hasAnyPendingApproverApproved(agreement) && hasValidChanges) {
      setIsResetApprovalModalVisible(true);
      return;
    }

    if (
      (isDraftApproved(agreement) || isDraftPartiallyApproved(agreement)) &&
      isDraft(agreement) &&
      hasValidChanges
    ) {
      setIsResetDraftApprovalModalVisible(true);
      return;
    }

    if (checked) {
      setIsSendModalVisible(true);
    } else {
      saveAgreementHandler();
    }
  };

  const apiError = getError(saveRpcState.error);

  return (
    <>
      <ResetDraftApprovalsWarning
        agreementId={agreementId}
        isOpen={isResetDraftApprovalModalVisible}
        onCancel={() => {
          setIsResetDraftApprovalModalVisible(false);
        }}
        onConfirm={() => {
          saveAgreementHandler();
          setIsResetDraftApprovalModalVisible(false);
        }}
      />
      <ResetApprovalsWarning
        agreementId={agreementId}
        isOpen={isResetApprovalModalVisible}
        onCancel={() => {
          setIsResetApprovalModalVisible(false);
        }}
        onConfirm={() => {
          saveAgreementHandler();
          setIsResetApprovalModalVisible(false);
        }}
      />
      <ResetSignaturesWarning
        agreementId={agreementId}
        isOpen={isResetSignatureModalVisible}
        onCancel={() => {
          setIsResetSignatureModalVisible(false);
        }}
        onConfirm={() => {
          setIsResetSignatureModalVisible(false);
          if (checked && isOwner) {
            setIsSendModalVisible(true);
          } else {
            saveAgreementHandler(
              getDefaultNotificationData(party, myParticipant, message) as NotificationData,
            );
          }
        }}
      />
      <ResetApprovalsSignaturesWarning
        agreementId={agreementId}
        isOpen={isResetApprovalSignatureModalVisible}
        onCancel={() => {
          setIsResetApprovalSignatureModalVisible(false);
        }}
        onConfirm={() => {
          setIsResetApprovalSignatureModalVisible(false);
          if (checked && isOwner) {
            setIsSendModalVisible(true);
          } else {
            saveAgreementHandler(
              getDefaultNotificationData(party, myParticipant, message) as NotificationData,
            );
          }
        }}
      />
      <SendContract
        agreementId={agreementId}
        onSaveAndNotifyChanges={saveAgreementHandler}
        isOpen={isSendModalVisible}
        onModalClose={() => {
          setIsSendModalVisible(false);
        }}
        isNotifyParticipants
        isSubjectFieldRequired={false}
        isMessageFieldRequired={false}
        messageTemplatesDisabled
        context="agreementOnesave"
        modalClassName={modalClassName}
      >
        {() => (
          <Button
            id={isInPreviewMode ? 'preview-save' : undefined}
            customClass={buttonClassName}
            kind={buttonKind ?? 'primary'}
            data-testid="confirm"
            disabled={disabled}
            onClick={handleSaveChanges}
          >
            {children}
          </Button>
        )}
      </SendContract>
      {apiError && (
        <Snackbar
          message={<ApiError customMessage={apiError} />}
          setMessage={resetSaveRpcState}
          severity={SEVERITY_TYPES.WARNING}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        />
      )}
    </>
  );
};

export default localize<Props>(SaveChangesButton);
